import { useState, type ChangeEvent, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { Bug, Sparkles, Wrench, Check, Copy, Paperclip, X } from 'lucide-react'
import type { Position, PublicRequestType, CreateRequestInput } from '@shared/schemas/requests'
import { createRequestSchema, positionEnum, ALLOWED_IMAGE_MIME, MAX_ATTACHMENT_BYTES } from '@shared/schemas/requests'
import { useDepartments, useSubmitRequest } from '@/lib/requests'
import { ApiError } from '@/lib/api'

const DRAFT_KEY = 'tar-ops-request-draft'

// Step 1 stays in English by design (see docs/DECISIONS.md); step 2 is
// fixed Arabic, not tied to the app's EN/AR toggle — this is a form for
// external companies, not the internal app shell.
const TYPES: { value: PublicRequestType; label: string; icon: typeof Bug }[] = [
  { value: 'feature', label: 'New feature request', icon: Sparkles },
  { value: 'bug', label: 'Bug report', icon: Bug },
  { value: 'enhancement', label: 'Enhancement', icon: Wrench },
]

const POSITION_LABEL: Record<Position, string> = {
  sales_agent: 'مندوب مبيعات',
  telesales_agent: 'مندوب تليسيلز',
  sales_team_leader: 'تيم ليدر مبيعات',
  telesales_team_leader: 'تيم ليدر تليسيلز',
  sales_manager: 'مدير مبيعات',
  telesales_manager: 'مدير تليسيلز',
  hr: 'موارد بشرية',
  hr_manager: 'مدير موارد بشرية',
}

interface FormState {
  type: PublicRequestType | null
  requesterName: string
  requesterEmail: string
  position: Position | ''
  team: string
  departmentId: string
  description: string
  attachmentDataUrl: string | undefined
  attachmentFileName: string | null
}

const EMPTY_STATE: FormState = {
  type: null,
  requesterName: '',
  requesterEmail: '',
  position: '',
  team: '',
  departmentId: '',
  description: '',
  attachmentDataUrl: undefined,
  attachmentFileName: null,
}

function loadDraft(): FormState {
  try {
    const raw = localStorage.getItem(DRAFT_KEY)
    return raw ? { ...EMPTY_STATE, ...JSON.parse(raw) } : EMPTY_STATE
  } catch {
    return EMPTY_STATE
  }
}

const inputClass =
  'w-full rounded-md border-2 border-ink-soft/25 bg-surface-2 px-3 py-2 text-sm text-ink outline-none placeholder:text-ink-soft/60 focus:border-brand'

export function RequestPage() {
  const { data: departments } = useDepartments()
  const submit = useSubmitRequest()

  const [step, setStep] = useState(1)
  const [form, setForm] = useState<FormState>(loadDraft)
  const [error, setError] = useState<string | null>(null)
  const [fileError, setFileError] = useState<string | null>(null)
  const [result, setResult] = useState<{ publicId: string } | null>(null)
  const [copied, setCopied] = useState(false)

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => {
      const next = { ...f, [key]: value }
      if (!result) {
        try {
          localStorage.setItem(DRAFT_KEY, JSON.stringify(next))
        } catch {
          // private window / blocked storage — draft just won't persist
        }
      }
      return next
    })
  }

  const onFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFileError(null)
    const file = e.target.files?.[0]
    if (!file) return

    if (!(ALLOWED_IMAGE_MIME as readonly string[]).includes(file.type)) {
      setFileError('صيغة الصورة غير مدعومة.')
      e.target.value = ''
      return
    }
    if (file.size > MAX_ATTACHMENT_BYTES) {
      setFileError('حجم الصورة كبير جدًا (الحد الأقصى 4 ميغابايت).')
      e.target.value = ''
      return
    }

    const reader = new FileReader()
    reader.onload = () => {
      set('attachmentDataUrl', reader.result as string)
      set('attachmentFileName', file.name)
    }
    reader.readAsDataURL(file)
  }

  const removeAttachment = () => {
    set('attachmentDataUrl', undefined)
    set('attachmentFileName', null)
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)

    const input: CreateRequestInput = {
      requesterName: form.requesterName,
      requesterEmail: form.requesterEmail,
      position: form.position as Position,
      team: form.team.trim() || undefined,
      departmentId: form.departmentId,
      type: form.type!,
      payload: {
        description: form.description,
        attachmentDataUrl: form.attachmentDataUrl,
      },
    }

    const parsed = createRequestSchema.safeParse(input)
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'يرجى تعبئة الاسم والشركة والوصف.')
      return
    }

    try {
      const res = await submit.mutateAsync(parsed.data)
      setResult({ publicId: res.request.publicId })
      try {
        localStorage.removeItem(DRAFT_KEY)
      } catch {
        // ignore
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'حدث خطأ أثناء الإرسال — حاول مرة أخرى.')
    }
  }

  if (result) {
    const trackUrl = `${window.location.origin}/track/${result.publicId}`
    return (
      <div className="mx-auto flex min-h-screen max-w-lg flex-col items-center justify-center gap-4 px-4 text-center" dir="rtl">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-ok/15 text-ok">
          <Check size={22} />
        </div>
        <h1 className="font-display text-2xl font-semibold">تم إرسال الطلب</h1>
        <p className="text-sm text-ink-soft">احفظ هذا الرابط لمتابعة طلبك في أي وقت.</p>
        <div className="flex w-full items-center gap-2 rounded-md border-2 border-ink-soft/25 bg-surface-2 px-3 py-2">
          <code className="flex-1 truncate text-start text-xs" dir="ltr">{trackUrl}</code>
          <button
            type="button"
            onClick={() => {
              navigator.clipboard?.writeText(trackUrl).catch(() => {})
              setCopied(true)
              setTimeout(() => setCopied(false), 2000)
            }}
            className="flex items-center gap-1 rounded-md bg-ink px-2.5 py-1.5 text-xs font-semibold text-paper"
          >
            <Copy size={12} /> {copied ? 'تم النسخ' : 'نسخ الرابط'}
          </button>
        </div>
        <button
          type="button"
          onClick={() => {
            setResult(null)
            setForm(EMPTY_STATE)
            setStep(1)
          }}
          className="text-sm font-semibold text-brand"
        >
          إرسال طلب آخر
        </button>
      </div>
    )
  }

  if (step === 1) {
    return (
      <div className="mx-auto min-h-screen max-w-xl px-4 py-10">
        <div className="mb-6 flex items-center gap-2.5">
          <img src="/brand/tar-group-logo.png" alt="TAR Group" className="h-6 w-auto" />
          <span className="font-display text-sm font-semibold tracking-wide">TAR Ops</span>
        </div>

        <h1 className="font-display text-2xl font-semibold">Send a request to AI Operations</h1>
        <p className="mt-1 text-sm text-ink-soft">
          No account needed — pick what this is, tell us the details, and track it with a private link.
        </p>

        <div className="mt-6 flex items-center gap-1.5" aria-hidden="true">
          {[1, 2].map((n) => (
            <div key={n} className={`h-1 flex-1 rounded-pill ${n <= step ? 'bg-brand' : 'bg-surface-2'}`} />
          ))}
        </div>
        <p className="mb-6 mt-1.5 text-xs font-semibold text-ink-soft">Step 1 of 2</p>

        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
          {TYPES.map(({ value, label, icon: Icon }) => (
            <button
              key={value}
              type="button"
              onClick={() => {
                set('type', value)
                setStep(2)
              }}
              className={`flex items-center gap-3 rounded-card border-2 p-4 text-start transition-colors ${
                form.type === value ? 'border-brand bg-brand/5' : 'border-ink-soft/20 bg-surface hover:border-ink-soft/40'
              }`}
            >
              <Icon size={18} className="flex-none text-brand" />
              <span className="font-semibold">{label}</span>
            </button>
          ))}
        </div>

        <Link to="/login" className="mt-10 block text-center text-xs text-ink-soft">
          TAR Ops
        </Link>
      </div>
    )
  }

  return (
    <div className="mx-auto min-h-screen max-w-xl px-4 py-10" dir="rtl">
      <div className="mb-6 flex items-center gap-2.5">
        <img src="/brand/tar-group-logo.png" alt="TAR Group" className="h-6 w-auto" />
        <span className="font-display text-sm font-semibold tracking-wide">TAR Ops</span>
      </div>

      <div className="mb-6 flex items-center gap-1.5" aria-hidden="true">
        {[1, 2].map((n) => (
          <div key={n} className={`h-1 flex-1 rounded-pill ${n <= step ? 'bg-brand' : 'bg-surface-2'}`} />
        ))}
      </div>
      <p className="mb-1 text-xs font-semibold text-ink-soft">الخطوة 2 من 2</p>
      <h1 className="mb-6 font-display text-xl font-semibold">
        {TYPES.find((t) => t.value === form.type)?.label}
      </h1>

      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-semibold uppercase tracking-wide text-ink-soft">الاسم</label>
          <input
            required
            className={inputClass}
            value={form.requesterName}
            onChange={(e) => set('requesterName', e.target.value)}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-semibold uppercase tracking-wide text-ink-soft">
            البريد الإلكتروني<span className="text-brand"> *</span>
          </label>
          <input
            type="email"
            required
            className={inputClass}
            value={form.requesterEmail}
            onChange={(e) => set('requesterEmail', e.target.value)}
            placeholder="لمتابعة طلبك"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-semibold uppercase tracking-wide text-ink-soft">الشركة أو القسم</label>
          <select required className={inputClass} value={form.departmentId} onChange={(e) => set('departmentId', e.target.value)}>
            <option value="" disabled>
              اختر الشركة أو القسم
            </option>
            {departments?.map((d) => (
              <option key={d.id} value={d.id}>
                {d.nameEn}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-semibold uppercase tracking-wide text-ink-soft">
            الوظيفة<span className="text-brand"> *</span>
          </label>
          <select
            required
            className={inputClass}
            value={form.position}
            onChange={(e) => set('position', e.target.value as Position)}
          >
            <option value="" disabled>
              اختر الوظيفة
            </option>
            {positionEnum.options.map((p) => (
              <option key={p} value={p}>
                {POSITION_LABEL[p]}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-semibold uppercase tracking-wide text-ink-soft">الفريق (اختياري)</label>
          <input
            className={inputClass}
            value={form.team}
            onChange={(e) => set('team', e.target.value)}
            placeholder="مثال: فريق التيم ليدر أحمد"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-semibold uppercase tracking-wide text-ink-soft">
            صف ما تحتاجه أو طلبك أو المشكلة
          </label>
          <textarea
            required
            rows={5}
            className={inputClass}
            value={form.description}
            onChange={(e) => set('description', e.target.value)}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-semibold uppercase tracking-wide text-ink-soft">
            إرفاق لقطة شاشة (اختياري)
          </label>
          {form.attachmentFileName ? (
            <div className="flex items-center gap-2 rounded-md border-2 border-ink-soft/25 bg-surface-2 px-3 py-2 text-sm">
              <Paperclip size={14} className="flex-none text-ink-soft" />
              <span className="flex-1 truncate">{form.attachmentFileName}</span>
              <button type="button" onClick={removeAttachment} className="text-ink-soft hover:text-critical">
                <X size={14} />
              </button>
            </div>
          ) : (
            <input type="file" accept="image/*" onChange={onFileChange} className="text-sm" />
          )}
          {fileError && <p className="text-xs text-critical">{fileError}</p>}
        </div>

        {error && <p className="text-sm text-critical">{error}</p>}

        <div className="flex justify-between pt-2">
          <button type="button" onClick={() => setStep(1)} className="text-sm font-semibold text-ink-soft">
            رجوع
          </button>
          <button
            type="submit"
            disabled={submit.isPending}
            className="rounded-md bg-brand px-4 py-2 text-sm font-semibold text-brand-ink disabled:opacity-60"
          >
            {submit.isPending ? 'جارٍ الإرسال…' : 'إرسال الطلب'}
          </button>
        </div>
      </form>
    </div>
  )
}
